import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { ResourceBlock } from 'stile-shared/src/components/2020/blocks/ResourceBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Card } from 'stile-shared/src/components/2020/common/Card';
import { P } from 'stile-shared/src/components/2020/common/Primitives';

import { getPageData } from 'utils/getPageData';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

function LearningAndTrainingPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/resources/planning_cover.png"
            alt=""
          ></StaticImage>
        }
      />

      <TextBlock>
        <P>
          Stile is a complete, coherent curriculum for Australian science classrooms. Our resources
          are designed to help students be the best learners they can be while supporting teachers
          to maximise their impact through evidence-based teaching strategies.
        </P>
        <P>
          These documents are for science leaders and educators who teach a science programme for
          Years 7-10. They offer a world-class starting point for designing your school’s science
          curriculum.
        </P>
        <P>
          These documents can be used in their current format alongside our comprehensive teaching
          plans to provide the support that graduate teachers need, or they can be customised to
          best suit your unique context and provide the flexibility that experienced teachers
          demand.
        </P>
      </TextBlock>

      <ResourceBlock
        title="Curriculum planning resources"
        text="Get a head start on planning for next year with our curriculum-aligned scope and sequences. These documents are for science leaders and educators to teach a Years 7-10 science program."
        variant="narrow"
        colorVariant="beige"
      >
        <Card
          title="Australian Curriculum Version 9.0"
          url="/resources/ac9.0-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/ac_9_2024.png"
              alt=""
            />
          }
        />

        <Card
          title="Australian Curriculum Version 8.4"
          url="/resources/ac8.4-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/ac_8.4_2024.png"
              alt=""
            />
          }
        />

        <Card
          title="New South Wales Syllabus"
          url="/resources/nsw-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/nsw_2024.png"
              alt=""
            />
          }
        />

        <Card
          title="New South Wales Syllabus and Implementation Guide"
          url="/resources/new-nsw-syllabus/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/nsw_2026.png"
              alt=""
            />
          }
        />

        <Card
          title="Victorian Curriculum"
          url="/resources/vic-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/vic_2024.png"
              alt=""
            />
          }
        />

        <Card
          title="Western Australian Curriculum"
          url="/resources/wa-2024/"
          image={
            /* image is sufficiently described by captions, so alt text is not required */
            <StaticImage
              src="../../../../stile-shared/assets/images/resources/wa_2024.png"
              alt=""
            />
          }
        />
      </ResourceBlock>

      <NextPageBlock path="/why-choose-stile/case-studies/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default LearningAndTrainingPage;
